import { profileFor } from "./profiles/index.js";
import type { AiToolId, HomeCardId, KpiId, PanelTabId, VerticalProfile } from "./profiles/types.js";

/**
 * b80: per-workspace module flags — the single source of truth for what a workspace *uses*
 * (nav, customer-panel tabs, home cards, AI tools). The business-type profile only seeds the
 * defaults; the owner's stored toggle (`Workspace.settings.enabled_modules`) wins, and plan
 * entitlements still cap the result.
 */
export const MODULE_IDS = [
  "accounts",
  "travel",
  "store",
  "quotes",
  "tables",
  "bookings",
  "products",
  "services",
  "documents",
] as const;
export type ModuleId = (typeof MODULE_IDS)[number];

/**
 * OWN-333: modules every workspace had before they became switchable. No profile suggests them;
 * with no stored/admin value they resolve `true`, so existing workspaces keep today's behaviour.
 */
const DEFAULT_ON_MODULES: ReadonlySet<ModuleId> = new Set<ModuleId>([
  "products",
  "services",
  "documents",
]);

/** OWN-333: a module is off while any module it needs is off (`products` off ⇒ `store` off). */
export const MODULE_DEPENDENCIES: Readonly<Partial<Record<ModuleId, readonly ModuleId[]>>> = {
  store: ["products"],
  bookings: ["services"],
};

/** Modules that switch off with `id` (for UI sentences like «يقفل المتجر أيضاً»). */
export function dependentsOf(id: ModuleId): readonly ModuleId[] {
  return MODULE_IDS.filter((m) => MODULE_DEPENDENCIES[m]?.includes(id) ?? false);
}

/** Money modules are never switched on by a default: only the owner's explicit `true` enables them. */
export const MONEY_MODULES: ReadonlySet<ModuleId> = new Set<ModuleId>(["accounts", "quotes"]);

export type StoredModules = Partial<Record<ModuleId, boolean>>;
export type ModuleFlags = Readonly<Record<ModuleId, boolean>>;

interface ModuleSurface {
  readonly panel: readonly PanelTabId[];
  readonly home: readonly HomeCardId[];
  readonly ai: readonly AiToolId[];
  /** Home KPIs (b82: `GET home/kpis` drops them with their module). */
  readonly kpis: readonly KpiId[];
  /** Nav feature keys hidden while the module is off (direct links and data keep working). */
  readonly nav: readonly string[];
  /** Settings section keys hidden while the module is off. */
  readonly settings?: readonly string[];
}

export const MODULE_SURFACES: Readonly<Record<ModuleId, ModuleSurface>> = {
  accounts: {
    panel: ["account", "payments_balance"],
    home: ["balances_due"],
    ai: ["get_my_balance"],
    kpis: ["outstanding_balance"],
    nav: [],
  },
  travel: {
    panel: ["trips", "travelers_documents"],
    home: ["trips_this_month", "deposits_waiting", "documents_missing"],
    ai: ["hold_seats"],
    kpis: ["trip_fill_rate", "trips_this_month", "deposits_collected"],
    // OWN-368: the trips tabs sit under Bookings and hide with the module.
    nav: ["trips/packages", "trips/bookings"],
  },
  store: {
    panel: ["orders", "cart"],
    home: ["orders_today", "abandoned_carts"],
    ai: [
      "search_products",
      "send_product",
      "send_products",
      "send_order_link",
      "check_order_status",
      "create_order",
    ],
    kpis: ["orders"],
    nav: ["store/orders", "store", "carts"],
  },
  quotes: {
    panel: ["quotes"],
    home: ["quotes_waiting"],
    ai: [],
    kpis: ["quotes_accepted"],
    nav: ["quotes", "quotes/settings"],
  },
  tables: {
    panel: ["table_booking"],
    home: ["tables_tonight"],
    ai: [],
    kpis: ["tables_tonight"],
    nav: [],
  },
  bookings: {
    panel: ["appointments", "viewings", "vehicle"],
    home: ["bookings_today", "no_shows", "viewings_this_week"],
    ai: ["book_slot"],
    kpis: ["bookings", "no_show_rate", "viewings"],
    nav: [
      "bookings",
      "bookings/services",
      "bookings/staff",
      "bookings/waitlist",
      "bookings/settings",
    ],
  },
  products: {
    panel: [],
    home: [],
    ai: ["search_products", "send_product", "send_products"],
    kpis: [],
    nav: ["catalog"],
  },
  services: { panel: [], home: [], ai: [], kpis: [], nav: ["bookings/services"] },
  documents: { panel: [], home: [], ai: [], kpis: [], nav: [], settings: ["settings/documents"] },
};

const profileUses = (p: VerticalProfile, m: ModuleId): boolean => {
  const s = MODULE_SURFACES[m];
  return (
    s.panel.some((x) => p.panel.includes(x)) ||
    s.home.some((x) => p.home.includes(x)) ||
    s.ai.some((x) => p.aiTools.includes(x))
  );
};

/** What the business-type profile suggests (UI hint in settings). Not what is enabled. */
export function suggestedModules(businessType: string | null | undefined): ModuleFlags {
  const p = profileFor(businessType ?? null);
  return Object.fromEntries(
    MODULE_IDS.map((m) => [m, DEFAULT_ON_MODULES.has(m) || profileUses(p, m)]),
  ) as ModuleFlags;
}

/** Tolerant read of `settings.enabled_modules`: only known ids with boolean values survive. */
export function readStoredModules(raw: unknown): StoredModules {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: StoredModules = {};
  for (const m of MODULE_IDS) {
    const v = (raw as Record<string, unknown>)[m];
    if (typeof v === "boolean") out[m] = v;
  }
  return out;
}

/** OWN-333: the platform admin's per-workspace decision (`settings.admin_modules`). */
export type AdminDecision = "on" | "off";
export type AdminModules = Partial<Record<ModuleId, AdminDecision>>;

export function isAdminDecision(v: unknown): v is AdminDecision {
  return v === "on" || v === "off";
}

/** Tolerant read of `settings.admin_modules`: only known ids with "on"/"off" survive. */
export function readAdminModules(raw: unknown): AdminModules {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: AdminModules = {};
  for (const m of MODULE_IDS) {
    const v = (raw as Record<string, unknown>)[m];
    if (isAdminDecision(v)) out[m] = v;
  }
  return out;
}

/**
 * enabled = plan cap ← admin decision ← owner toggle ← profile suggestion (money modules: owner
 * `true` or admin "on" only), then dependencies. `plan` lists modules the plan entitles; absent = no cap.
 */
export function resolveModules(input: {
  readonly businessType: string | null | undefined;
  readonly stored?: StoredModules | undefined;
  readonly admin?: AdminModules | undefined;
  readonly plan?: Partial<Record<ModuleId, boolean>> | undefined;
}): ModuleFlags {
  const suggested = suggestedModules(input.businessType);
  const stored = input.stored ?? {};
  const admin = input.admin ?? {};
  const own = (m: ModuleId): boolean => {
    const decision = admin[m];
    if (decision) return decision === "on";
    return MONEY_MODULES.has(m) ? stored[m] === true : (stored[m] ?? suggested[m]);
  };
  const base = Object.fromEntries(
    MODULE_IDS.map((m) => [m, own(m) && input.plan?.[m] !== false]),
  ) as Record<ModuleId, boolean>;
  return Object.fromEntries(
    MODULE_IDS.map((m) => [m, base[m] && (MODULE_DEPENDENCIES[m] ?? []).every((d) => base[d])]),
  ) as ModuleFlags;
}

export interface WorkspaceModules {
  readonly stored: StoredModules;
  readonly admin: AdminModules;
  readonly suggested: ModuleFlags;
  readonly enabled: ModuleFlags;
  /** Admin-decided modules: "on" = locked on, "off" = hidden from the owner. */
  readonly locks: AdminModules;
}

/** OWN-333: the one module resolution, straight from `Workspace.settings`. */
export function resolveWorkspaceModules(input: {
  readonly businessType: string | null | undefined;
  readonly settings: unknown;
  readonly plan?: Partial<Record<ModuleId, boolean>> | undefined;
}): WorkspaceModules {
  const settings =
    input.settings && typeof input.settings === "object" && !Array.isArray(input.settings)
      ? (input.settings as Record<string, unknown>)
      : {};
  const stored = readStoredModules(settings.enabled_modules);
  const admin = readAdminModules(settings.admin_modules);
  return {
    stored,
    admin,
    suggested: suggestedModules(input.businessType),
    enabled: resolveModules({ businessType: input.businessType, stored, admin, plan: input.plan }),
    locks: admin,
  };
}

/** OWN-333: admin presets (data only; the admin UI names them). */
export interface ModuleTemplate {
  readonly id: string;
  readonly modules: AdminModules;
}

export const MODULE_TEMPLATES: Readonly<Record<string, ModuleTemplate>> = {
  services_no_booking: {
    id: "services_no_booking",
    modules: { bookings: "off", services: "on" },
  },
  store_no_quotes: { id: "store_no_quotes", modules: { quotes: "off", documents: "on" } },
  support_only: {
    id: "support_only",
    modules: Object.fromEntries(MODULE_IDS.map((m) => [m, "off"])) as AdminModules,
  },
};

/** Tools that belong to a disabled module; drop them from an allowlist. */
export function toolsOfDisabledModules(flags: ModuleFlags): ReadonlySet<string> {
  return new Set(MODULE_IDS.filter((m) => !flags[m]).flatMap((m) => MODULE_SURFACES[m].ai));
}

export function panelTabAllowed(flags: ModuleFlags | null | undefined, tab: PanelTabId): boolean {
  if (!flags) return true;
  return MODULE_IDS.every((m) => flags[m] || !MODULE_SURFACES[m].panel.includes(tab));
}
export function homeCardAllowed(flags: ModuleFlags | null | undefined, card: HomeCardId): boolean {
  if (!flags) return true;
  return MODULE_IDS.every((m) => flags[m] || !MODULE_SURFACES[m].home.includes(card));
}
export function navKeyAllowed(flags: ModuleFlags | null | undefined, key: string): boolean {
  if (!flags) return true;
  return MODULE_IDS.every((m) => flags[m] || !MODULE_SURFACES[m].nav.includes(key));
}

export function settingsKeyAllowed(flags: ModuleFlags | null | undefined, key: string): boolean {
  if (!flags) return true;
  return MODULE_IDS.every((m) => flags[m] || !(MODULE_SURFACES[m].settings ?? []).includes(key));
}

export function kpiAllowed(flags: ModuleFlags | null | undefined, kpi: KpiId): boolean {
  if (!flags) return true;
  return MODULE_IDS.every((m) => flags[m] || !MODULE_SURFACES[m].kpis.includes(kpi));
}

/**
 * b82: money modules the business type's profile lists but the owner has not switched on yet
 * (the owner prompt offers them). Empty when flags are unknown (legacy workspaces gate nothing).
 */
export function offeredMoneyModules(
  businessType: string | null | undefined,
  flags: ModuleFlags | null | undefined,
): readonly ModuleId[] {
  if (!flags) return [];
  const suggested = suggestedModules(businessType);
  return MODULE_IDS.filter((m) => MONEY_MODULES.has(m) && suggested[m] && !flags[m]);
}

/** Panel tabs of the profile hidden only because their module is off (owner hint). */
export function hiddenByModules(
  businessType: string | null | undefined,
  flags: ModuleFlags | null | undefined,
): readonly PanelTabId[] {
  return profileFor(businessType ?? null).panel.filter((id) => !panelTabAllowed(flags, id));
}
